import * as React from 'react';
import { Log, Telemetry } from 'core/infrastructure';
import * as ThemeManager from './ThemeManager';

import { Electron } from 'pos/common';
import { Box, HStack, Stack, StackProps, VStack } from '@chakra-ui/react';
import { BaseComponent } from 'core/ui';
import { Modal, ModalUtils, ModalHooks, useOverlayExecutor } from 'core/ui-next/modal';
import { LoadingBar } from 'core/ui-next';
import { Header } from './Header';
import { HQuickActionsPanel } from './QuickActionsPanel';
import { Res } from 'core/api';
import * as Images from '../Images';
import { ScreenSize, ScreenSizeContext } from './ScreenSize';
import { Footer } from './Footer';
import { ActionButtonDefinition } from './ActionButton';
import { Keypad } from './Keypad';
import { ActionQueue } from 'core/utils';


interface OverlayExecutorHandle {
	execute: <T>(args: {
		action: () => Promise<T>;
		overlay?: 'on-start' | 'on-first-request';
		waitForPendingRequests?: boolean;
	}) => Promise<T>;
}

const OverlayExecutor = React.forwardRef<OverlayExecutorHandle, EmptyProps>((_props, ref) => {

	const { execute } = useOverlayExecutor();

	React.useImperativeHandle(ref, () => ({
		execute,
	}));

	// No renderiza nada visual, simplemente sirve para usar la función execute desde ClassComponents que usamos en las clases hijas para algunos casos. Como
	// no podemos usar userOverlayExecutor en estos casos, nos valemos de esta artimaña para que las clases hijas y legacy puedan hacer this.execute(...).
	// Esto el día de mañana se podrá borrar,  todo debería usarse desde useOverlayExecutor dentro de FC. Lo dejamos por compatibilidad con pantallas antiguas.
	return null;
});


type ExtractGeneric<T> = T extends Modal.Actions<infer X> ? X : never;

export abstract class PosScreenClassComponent<P extends Modal.Actions<any>, S> extends BaseComponent<P, S> {

	declare context: React.ContextType<typeof ModalHooks.ModalContext>;
	static contextType = ModalHooks.ModalContext;

	private actionQueue = new ActionQueue();
	private overlayExecutorRef = React.createRef<OverlayExecutorHandle>();
	private screenRef = React.createRef<HTMLDivElement>();

	protected cancel = () => this.props.cancel?.();
	protected close = (value: ExtractGeneric<P>) => this.props.close?.(value);

	constructor(props: P) {
		super(props);
	}

	componentDidMount() {
		if (this.registerDismissHandlers()) {
			this.context.setDismissHandler(async () => await this.requestDismiss());
		}

		super.componentDidMount();

		Log.debug(`screen["${this.getScreenId()}"].show()`);
		Telemetry.screen(this.getScreenId());
	}

	componentWillUnmount(): void {
		this.actionQueue.clear();

		if (Electron.isEnabled()) {
			Electron.Keyboard.hide();
		}

		super.componentWillUnmount();
	}

	protected abstract getContent(): React.ReactNode;

	protected isOnTop(): boolean {
		return ModalUtils.legacy_isOnTop(this.screenRef.current!);
	}

	protected useKeypad(): boolean {
		return true;
	}

	protected useKeyboard(): boolean {
		return true;
	}

	protected isCloseHeaderButtonHidden(): boolean {
		return false;
	}

	protected getOpacity(): number {
		return 1;
	}

	protected isLegacy(): boolean {
		return true;
	}

	protected registerDismissHandlers(): boolean {
		return true;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected isLargeDevice(): boolean {
		return ThemeManager.isLargeDevice();
	}

	// ID único de la pantalla para telemetría y logging
	protected abstract getScreenId(): string;
	protected abstract getTitle(): string;
	protected abstract getImage(): string | SvgIcon;
	protected abstract getActionButtonsDefs(): Nullable<ActionButtonDefinition>[];

	protected okButton(action: () => Promise<void>): ActionButtonDefinition {
		return { title: Res.Common.OK_BTN(), image: Images.Ok, action, intent: 'ok' };
	}

	protected async requestDismiss(): Promise<void> {
		this.cancel();
	}

	protected cancelButton(action?: () => Promise<void>): ActionButtonDefinition {
		const actionHandler = action || (async () => this.cancel());
		return { title: Res.Common.CANCEL_BTN(), image: Images.Cancel, action: actionHandler, intent: 'cancel' };
	}

	protected okCancelButtons(actions: { onOK: () => Promise<void>, onCancel?: () => Promise<void> }): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(actions.onCancel),
			null,
			null,
			null,
			null,
			this.okButton(actions.onOK)
		];
	}

	getScreenRef() {
		return this.screenRef;
	}

	protected getHeader(): React.ReactNode {
		return <Header
			title={this.getTitle()}
			image={this.getImage()}
			onClose={async () => await this.requestDismiss()}
			showKeyboard={this.useKeyboard()}
			hideCloseButton={this.isCloseHeaderButtonHidden()}
		/>;
	}

	protected getFooter(): React.ReactNode {
		// En las pantallas de teléfono siempre pintamos los botones pequeños y no quitamos los botones de volver/cancelar
		// En el resto, quitamos los botones de volver/cancelar si no son full-screen, porque ya tienen la X de cerrar el modal

		const removeCancelButton = !ThemeManager.isPhone() && this.getSize() !== 'full-screen';
		const replaceCancelByBackButton = ThemeManager.isPhone();
		return <Footer><HQuickActionsPanel surface={'legacy-footer'} buttons={this.getActionButtonsDefs()} removeCancelButton={removeCancelButton} replaceCancelByBackButton={replaceCancelByBackButton} /></Footer>;
	}

	protected getSidebar(): React.ReactNode {
		return null;
	}

	public execute = async <T,>(args: {
		waitForPendingRequests?: boolean,
		overlay?: 'on-start' | 'on-first-request'
		action: () => Promise<T>
	}): Promise<T> => {
		return this.overlayExecutorRef.current!.execute(args);
	};

	setStateAsync<K extends keyof typeof this.state>(
		newState: Pick<typeof this.state, K> | ((prevState: Readonly<typeof this.state>, props: Readonly<typeof this.props>) => Pick<typeof this.state, K>),
		callback?: () => Promise<void> | void
	): Promise<void> {
		return new Promise(resolve => {
			if (!this.mounted) {
				return resolve();
			}

			this.setState(newState, async () => {
				if (callback) await callback();
				resolve();
			});
		});
	}

	async enqueue(action: () => Promise<void>) {
		await this.actionQueue.enqueue(action);
	}


	render() {

		const Header = this.getHeader();
		const Sidebar = this.getSidebar();
		const Footer = this.getFooter();
		const Content = this.getContent();

		const size = ThemeManager.isPhone() ? 'full-screen' : this.getSize();
		const { width, height } = getPhysicalSize(size);

		return <ScreenSizeContext.Provider value={size}>
			<VStack ref={this.screenRef} overflow={'hidden'} style={{ opacity: this.getOpacity() }} gap={0} h={height} w={width} userSelect={'none'}>
				{Header}
				<HStack w={'full'} h={'full'} gap={0}>
					{Sidebar}
					{/* Aquí hace falta dejar el width a 0 para que el content se pueda repartir por todo el espacio,
				ya que este elemento, por sí solo, no ocupa espacio horizontal, así que todo el espacio que le asignes
				será por el flexGrow */ }
					<Stack w={'0'} h={'full'} gap={0} flexGrow={1}>
						<Box flex={'0 0 auto'}>
							<LoadingBar />
						</Box>
						<Box flex={'1 1 auto'} h={'0'} bg={this.isLegacy() ? undefined : { base: 'white', _dark: '{colors.neutral.900}' }} className={this.isLegacy() ? 'legacy-styles chakra-screen-content' : 'chakra-screen-content'}>
							<Box h={'full'} overflow={this.isLegacy() ? 'auto' : undefined}>
								{Content}
							</Box>
						</Box>
						<Box flex={'0 0 auto'} shadow={'md-up'} zIndex={'1'}>
							{Footer}
							{
								this.useKeypad() && (
									<Keypad isFullScreen={size === 'full-screen'} isLargeDevice={this.isLargeDevice()} />
								)
							}
						</Box>
					</Stack>
				</HStack>
				<OverlayExecutor ref={this.overlayExecutorRef} />
			</VStack>
		</ScreenSizeContext.Provider>;
	}
}

export abstract class PosStatelessScreenClassComponent<P extends Modal.Actions<any>> extends PosScreenClassComponent<P, EmptyState> {
	createInitialState(_props: P) {
		return null;
	}
}

function getPhysicalSize(size?: ScreenSize): { width: StackProps['width'], height: StackProps['height'] } {

	const modalSize = size;

	if (modalSize === 'small') {
		return { width: '375px', height: '690px' };
	} else if (modalSize === 'medium') {
		return { width: '800px', height: '690px' };
	} else if (modalSize === 'full-screen') {
		return { width: '100dvw', height: '100dvh' };
	} else {
		return { width: modalSize?.width ? `${modalSize.width}px` : undefined, height: modalSize?.height ? `${modalSize.height}px` : undefined };
	}
}
